import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useFetch, PageHeader, Loading, EmptyState, ErrorState, SectionCard, Paginator, StatusChip, ExportButton } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

function Categories() {
  const { data, loading, error, refetch } = useFetch("/catalog/categories", []);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await api.post("/admin/categories", { name: name.trim() }); toast.success("Category created"); setName(""); setOpen(false); refetch(); }
    catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  const remove = async (c) => {
    try { await api.delete(`/admin/categories/${c.id}`); toast.success("Category deactivated"); refetch(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" data-testid="add-category-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Category</Button></DialogTrigger>
          <DialogContent data-testid="category-dialog">
            <DialogHeader><DialogTitle>New Category</DialogTitle></DialogHeader>
            <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Snacks" className="mt-1.5" data-testid="category-name-input" /></div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} disabled={busy} data-testid="category-save-btn">{busy ? "Saving…" : "Create"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> :
        !data?.length ? <SectionCard className="p-2"><EmptyState label="No categories" /></SectionCard> : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="categories-grid">
            {data.map((c) => (
              <SectionCard key={c.id} className="p-4 flex items-center justify-between" data-testid={`category-${c.id}`}>
                <div><div className="font-semibold">{c.name}</div><div className="text-xs text-muted-foreground font-mono">{c.slug}</div></div>
                <Button size="icon" variant="ghost" onClick={() => remove(c)} data-testid={`category-delete-${c.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </SectionCard>
            ))}
          </div>
        )}
    </div>
  );
}

const EMPTY_EDIT = { name: "", price: "", discount_percent: "", stock: "", category_id: "", is_active: true };

function Products() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const limit = 20;
  const path = `/catalog/products?page=${page}&limit=${limit}${q ? `&search=${encodeURIComponent(q)}` : ""}`;
  const { data, loading, error, refetch, setData } = useFetch(path, [path]);
  const cats = useFetch("/catalog/categories", []);
  const [edit, setEdit] = useState(null); // product being edited
  const [form, setForm] = useState(EMPTY_EDIT);
  const [busy, setBusy] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const catName = (id) => cats.data?.find((c) => c.id === id)?.name || "—";

  const openEdit = (p) => {
    setForm({ name: p.name || "", price: p.price ?? "", discount_percent: p.discount_percent ?? "", stock: p.stock ?? "", category_id: p.category_id || "", is_active: p.is_active !== false });
    setEdit(p);
  };
  const save = async () => {
    setBusy(true);
    try {
      const patch = {
        name: form.name, category_id: form.category_id || undefined,
        price: form.price === "" ? undefined : Number(form.price),
        discount_percent: form.discount_percent === "" ? undefined : Number(form.discount_percent),
        stock: form.stock === "" ? undefined : Number(form.stock),
        is_active: form.is_active,
      };
      const { data: updated } = await api.put(`/admin/products/${edit.id}`, patch);
      setData({ ...data, items: data.items.map((x) => (x.id === edit.id ? { ...x, ...updated } : x)) });
      toast.success("Product updated");
      setEdit(null);
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  const toggleActive = async (p) => {
    try {
      const next = !(p.is_active !== false);
      const { data: updated } = await api.put(`/admin/products/${p.id}`, { is_active: next });
      setData({ ...data, items: data.items.map((x) => (x.id === p.id ? { ...x, ...updated } : x)) });
      toast.success(`Product ${next ? "activated" : "deactivated"}`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const columns = [
    { key: "name", label: "Name" }, { key: "price", label: "Price" }, { key: "discount_percent", label: "Discount%" },
    { key: "tax_rate", label: "GST%" }, { key: "stock", label: "Stock" },
    { key: "category", label: "Category", get: (r) => catName(r.category_id) },
    { key: "is_active", label: "Active", get: (r) => (r.is_active ? "Yes" : "No") }, { key: "id", label: "Product ID" },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <form onSubmit={(e) => { e.preventDefault(); setPage(1); setQ(search); }} className="flex-1 flex gap-2">
          <Input placeholder="Search products…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="products-search-input" />
          <Button type="submit" variant="outline" data-testid="products-search-btn">Search</Button>
        </form>
        <ExportButton filename="sonu-products.csv" rows={data?.items} columns={columns} testid="export-products-btn" />
      </div>
      <SectionCard className="overflow-hidden">
        {loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> :
          !data?.items?.length ? <EmptyState label="No products" /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="products-table">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr><th className="px-4 py-2.5 font-semibold">Product</th><th className="px-4 py-2.5 font-semibold">Price</th><th className="px-4 py-2.5 font-semibold">Disc%</th><th className="px-4 py-2.5 font-semibold">GST%</th><th className="px-4 py-2.5 font-semibold">Stock</th><th className="px-4 py-2.5 font-semibold">Category</th><th className="px-4 py-2.5 font-semibold">Active</th><th className="px-4 py-2.5 font-semibold text-right">Edit</th></tr>
                </thead>
                <tbody className="divide-y">
                  {data.items.map((p) => (
                    <tr key={p.id} className="hover:bg-muted/30" data-testid={`product-row-${p.id}`}>
                      <td className="px-4 py-2.5 font-medium">{p.name}{p.is_new_arrival && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">NEW</span>}</td>
                      <td className="px-4 py-2.5 tnum">₹{Number(p.price).toFixed(2)}</td>
                      <td className="px-4 py-2.5 tnum">{p.discount_percent || 0}%</td>
                      <td className="px-4 py-2.5 tnum">{p.tax_rate ?? "—"}</td>
                      <td className="px-4 py-2.5 tnum">{p.stock}</td>
                      <td className="px-4 py-2.5 text-xs">{catName(p.category_id)}</td>
                      <td className="px-4 py-2.5"><Switch checked={p.is_active !== false} onCheckedChange={() => toggleActive(p)} data-testid={`product-active-${p.id}`} /></td>
                      <td className="px-4 py-2.5 text-right"><Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`product-edit-btn-${p.id}`}><Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </SectionCard>
      <Paginator page={page} total={data?.total} limit={limit} onPage={setPage} />

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="product-edit-dialog">
          <DialogHeader><DialogTitle>Edit “{edit?.name}”</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setF("name", e.target.value)} className="mt-1.5" data-testid="product-name-input" /></div>
            <div><Label>Price ₹</Label><Input type="number" value={form.price} onChange={(e) => setF("price", e.target.value)} className="mt-1.5" data-testid="product-price-input" /></div>
            <div><Label>Discount %</Label><Input type="number" value={form.discount_percent} onChange={(e) => setF("discount_percent", e.target.value)} className="mt-1.5" data-testid="product-discount-input" /></div>
            <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setF("stock", e.target.value)} className="mt-1.5" data-testid="product-stock-input" /></div>
            <div><Label>Category</Label>
              <Select value={form.category_id} onValueChange={(v) => setF("category_id", v)}>
                <SelectTrigger className="mt-1.5" data-testid="product-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{cats.data?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex items-center justify-between border rounded-lg px-3 py-2 mt-1">
              <span className="text-sm font-medium">Active</span>
              <Switch checked={form.is_active} onCheckedChange={(v) => setF("is_active", v)} data-testid="product-active-switch" />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="product-save-btn">{busy ? "Saving…" : "Save changes"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Catalog() {
  return (
    <div>
      <PageHeader title="Catalog" subtitle="Manage categories and edit any shop's products" />
      <Tabs defaultValue="categories">
        <TabsList data-testid="catalog-tabs">
          <TabsTrigger value="categories" data-testid="catalog-tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="products" data-testid="catalog-tab-products">Products</TabsTrigger>
        </TabsList>
        <TabsContent value="categories" className="mt-4"><Categories /></TabsContent>
        <TabsContent value="products" className="mt-4"><Products /></TabsContent>
      </Tabs>
    </div>
  );
}
